import { Auth } from 'aws-amplify';
import React, { FC, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Input, notification, Button } from 'src/common';
import { useNavigate } from 'react-router-dom';
import { Paths } from 'src/pages/paths';
import styles from '../index.module.scss';
import AuthBack from '../AuthBack';
import { useApp } from 'src/models/auth';

interface FormState {
  email: string;
}

export const ForgotPasswordPage: FC = () => {
  const { t } = useTranslation();
  const { email, setResult } = useApp();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);

  const initialValues: FormState = useMemo(() => ({ email: email ?? '' }), [email]);

  const onFinish = useCallback(
    (values: FormState) => {
      const { email } = values;

      setIsLoading(true);
      Auth.forgotPassword(email)
        .then(() => {
          setResult(prev => ({ ...prev, email }));
          notification.success({
            message: t('Auth.checkEmailForCode'),
            description: t('Auth.checkEmailForCodeDescription'),
            duration: 1.5,
            onClose: () => {
              setIsLoading(false);
              navigate(Paths.RESET_PASSWORD, { replace: true });
            }
          });
        })
        .catch(error => {
          notification.error({
            message: t('Auth.codeNotSent'),
            description: t(
              error.code === 'NotAuthorizedException' ? 'Auth.expiredTemporaryPasswordReset' : 'Common.unknownErrorDesc'
            )
          });

          setIsLoading(false);
        });
    },
    [navigate, setResult, t]
  );

  return (
    <>
      <Form form={form} onFinish={onFinish} initialValues={initialValues} layout={'vertical'} className={styles.form}>
        <div className={styles.heading}>
          <h3 className={styles.title}>
            <span>{t('Auth.forgot.title')}</span>
          </h3>
          <span className={styles.desc}>{t('Auth.forgot.desc')}</span>
        </div>
        <Form.Item
          name="email"
          label={t('Auth.emailAddress')}
          rules={[{ required: true, message: t('Auth.enterEmail') }]}
        >
          <Input name="email" value={email} placeholder={t('Common.email')} autoComplete={'username'} type={'email'} />
        </Form.Item>
        <div className={styles.actions}>
          <Button htmlType={'submit'} disabled={isLoading} type={'primary'} loading={isLoading}>
            {t('Auth.forgot.buttons.submit')}
          </Button>
          <AuthBack />
        </div>
      </Form>
    </>
  );
};

export default ForgotPasswordPage;
